import React, { useState, useEffect } from "react";

import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAccount,
} from "@solana/spl-token";
import { connection } from "../../../constants/constants";
import {
  Transaction,
  LAMPORTS_PER_SOL,
  SystemProgram,
  PublicKey,
} from "@solana/web3.js";
import { Program, AnchorProvider, BN } from "@coral-xyz/anchor";
import { PriceServiceConnection } from "@pythnetwork/price-service-client";
import { PythSolanaReceiver } from "@pythnetwork/pyth-solana-receiver";
import IDL from "../../../constants/token_idl.json";

import { useWallet } from "@solana/wallet-adapter-react";

import axios from "axios";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import SolanaPrice from "../../../components/websocket/SolanaPrice";
import messages from "../../../constants/messages";
import useContractInit from "../../../utils/hooks/contractInit";
import useGetTokenQuantity from "../../../utils/hooks/getGoesOutToken";

const SOL_PRICE_FEED_ID =
  "0xef0d8b6fda2ceba41da15d4095d1da392a0d2f8ed0c6c7bc0f4cfac8c280b56d";

const HERMES_URL = "https://hermes.pyth.network";

const HERMES_URL_API = "https://hermes.pyth.network/v2/updates/price/latest";

const tokenPrice = process.env.REACT_APP_TOKEN_PRICE;

export const TokenSaleApp = ({
  wallet,
  isOpen,
  setIsOpen,
  balance,
  walletAddress,
}) => {
  const provider = new AnchorProvider(connection, wallet, {
    preflightCommitment: "processed",
  });

  const { connected } = useWallet();
  const {
    tokenSaleConfigAddress,
    tokenAuthority,
    tokenMintAddress,
    solanaReceiverAddress,
    saleAuthority,
    getTokenInitializationDetails,
  } = useContractInit();
  const { currentSlot, loadingState, getGoesOutToken, totalBuyable } =
    useGetTokenQuantity();
  const [program, setProgram] = useState(null);
  const [solValue, setSolValue] = useState("");
  const [isLimit, setIsLimit] = useState(false);
  const [tokenLimit, setTokenLimit] = useState("");
  const [calculated, setCalculated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tokenValue, setTokenValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const user = useSelector((state) => state.userStore.user);
  const token = user.auth;

  const REACT_APP_EXPLORER_BASE_URL = process.env.REACT_APP_EXPLORER_BASE_URL;
  const REACT_APP_TOKEN_ENVIRONMENT = process.env.REACT_APP_TOKEN_ENVIRONMENT;

  const transaction = new Transaction();

  // Initialize Anchor program
  useEffect(() => {
    const initializeProgram = async () => {
      if (wallet) {
        const programInstance = new Program(IDL, provider);

        setProgram(programInstance);
      } else {
        return;
      }
    };

    initializeProgram();
  }, [wallet]);

  // Buy Tokens Handler
  const handleBuyTokens = async () => {
    if (!program || !wallet) {
      Swal.fire({
        title: "Error!",
        text: "Please connect wallet",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      if (tokenValue) {
        console.log("totalBuyable???????????????", totalBuyable);
        if (totalBuyable > tokenValue) {
          setLoading(true);
          const priceServiceConnection = new PriceServiceConnection(
            HERMES_URL,
            {
              priceFeedRequestConfig: { binary: true },
            }
          );
          const pythSolanaReceiver = new PythSolanaReceiver({
            connection,
            wallet: wallet,
          });

          const priceUpdateData = await priceServiceConnection.getLatestVaas([
            SOL_PRICE_FEED_ID,
          ]);

          // Prepare token accounts
          const [associatedTokenAccount, tokenAccountCreatoinInstruction] =
            await getAssociatedTokenAccount(
              tokenMintAddress,
              wallet.publicKey,
              wallet.publicKey
            );

          if (tokenAccountCreatoinInstruction) {
            transaction.add(tokenAccountCreatoinInstruction);
          }

          const [programTokenAccount, programTokenAccountInstruction] =
            await getAssociatedTokenAccount(
              tokenMintAddress,
              saleAuthority,
              wallet.publicKey
            );

          if (programTokenAccountInstruction) {
            transaction.add(programTokenAccountInstruction);
          }

          const account = await getAccount(connection, programTokenAccount);

          console.log("Contract balance is: ", account.amount);

          const solPrice = await getPriceDetails();

          const estimatedTokens = calculateTokenAmount(solValue, solPrice);

          if (account.amount < estimatedTokens * 1000000) {
            Swal.fire({
              title: "Error!",
              text: "Insufficient tokens in the contract",
              icon: "error",
              confirmButtonText: "OK",
            });
            setLoading(false);
            return;
          }

          const txBuilder = pythSolanaReceiver.newTransactionBuilder({
            closeUpdateAccounts: true,
          });

          await txBuilder.addPostPriceUpdates(priceUpdateData);

          const [monthlyLimitsAccount, monthlyLimitsBump] =
            PublicKey.findProgramAddressSync(
              [Buffer.from("monthly_limits_a")],
              program.programId
            );

          const state = await program.account.monthlyLimits.fetch(
            monthlyLimitsAccount
          );

          state.limits.map((value, key) =>
            console.log("Limit for ", key, "month is: ", value.toNumber() / 1e6)
          );

          const currentTimestamp = new BN(Math.floor(Date.now() / 1000));

          // Mounth For testing token

          let monthNumber = 0;

          const nextMonthTimestamp = currentTimestamp.add(
            new BN(monthNumber * 2629743)
          );

          // Convert SOL to lamports
          const solAmountLamports = LAMPORTS_PER_SOL * solValue;

          await txBuilder.addPriceConsumerInstructions(
            async (getPriceUpdateAccount) => {
              return [
                ...(tokenAccountCreatoinInstruction
                  ? [
                      {
                        instruction: tokenAccountCreatoinInstruction,
                        signers: [],
                        programId: TOKEN_PROGRAM_ID,
                      },
                    ]
                  : []),

                {
                  instruction: await program.methods
                    .buyTokens(new BN(solAmountLamports))
                    .accounts({
                      buyer: wallet.publicKey,
                      saleAuthority: solanaReceiverAddress,
                      programSaleAuthority: saleAuthority,
                      saleConfig: tokenSaleConfigAddress,
                      authority: tokenAuthority,
                      mint: tokenMintAddress,
                      programTokenAccount: programTokenAccount,
                      buyerTokenAccount: associatedTokenAccount,
                      priceUpdate: getPriceUpdateAccount(SOL_PRICE_FEED_ID),
                      monthlyLimits: monthlyLimitsAccount,
                      systemProgram: SystemProgram.programId,
                      tokenProgram: TOKEN_PROGRAM_ID,
                      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
                    })
                    .instruction(),
                  signers: [],
                },
              ];
            }
          );

          const tx = await pythSolanaReceiver.provider.sendAll(
            await txBuilder.buildVersionedTransactions({
              computeUnitPriceMicroLamports: 50000,
            }),
            {
              skipPreflight: true,
            }
          );

          await connection.confirmTransaction(tx[2], "confirmed");

          const details = await getTransactionDetails(tx[2]);
          details.userId = user.userId;
          await sendTransactionDetails(details);
          await getGoesOutToken();
          setLoading(false);
          setSolValue("");
          setTokenValue("");
          setCalculated(false);
        } else {
          Swal.fire({
            title: "Limit Exceeded",
            text: "Buy token limit exceeded. please try with lesser sol amount",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } else {
        setError("No Tokens found");
      }
    } catch (error) {
      if (error?.message === "User rejected the request.") {
        Swal.fire({
          title: "Error!",
          text: "Transaction has been cancelled",
          icon: "error",
          confirmButtonText: "OK",
        });
        setLoading(false);
      } else {
        const latestSig = await getLatestWalletTx();
        const details = await getTransactionDetails(latestSig[0].signature);
        details.userId = user?.userId;

        await sendTransactionDetails(details);

        Swal.fire({
          title: "Error!",
          text: "Your Transaction has been failed. Please try again",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
      setLoading(false);
      setTokenValue("");
      setCalculated(false);
    }
  };

  const getLatestWalletTx = async () => {
    try {
      let limit = 1;

      const signature = await connection.getSignaturesForAddress(
        wallet.publicKey,
        { limit }
      );

      return signature;
    } catch (error) {
      console.error("Failed to fetch signature", error);
    }
  };

  const getTransactionDetails = async (signature) => {
    try {
      const transaction = await connection.getTransaction(signature, {
        maxSupportedTransactionVersion: 0,
      });

      const status = await getTransactionStatus(signature);

      if (transaction) {
        const buyAtDate = new Date(transaction.blockTime * 1000);
        const walletAddress = wallet.publicKey.toBase58();
        const fee = transaction.meta.fee / LAMPORTS_PER_SOL;
        let updatedTokenBalance = 0;

        let previousTokenBalance = 0;
        if (transaction.meta.preTokenBalances.length > 1) {
          previousTokenBalance =
            transaction.meta.preTokenBalances[1].uiTokenAmount.uiAmount;
          updatedTokenBalance =
            transaction.meta.postTokenBalances[1].uiTokenAmount.uiAmount;
        } else {
          updatedTokenBalance =
            transaction.meta.postTokenBalances[0].uiTokenAmount.uiAmount;
        }
        const tokenQuantity = updatedTokenBalance - previousTokenBalance;

        const presSolBalance = transaction.meta.preBalances[0];
        const postSolBalances = transaction.meta.postBalances[0];

        const changePythReciever =
          transaction.meta.preBalances[5] - transaction.meta.postBalances[5];

        const changeWormHoleReciever =
          transaction.meta.preBalances[6] - transaction.meta.postBalances[6];

        let solanaReceived =
          transaction.meta.postBalances[1] - transaction.meta.preBalances[1];

        let createAccountFee = 0;
        const checkAccount =
          transaction.transaction.message.staticAccountKeys[1].toBase58();
        if (checkAccount !== solanaReceiverAddress.toBase58()) {
          createAccountFee = transaction.meta.postBalances[1];
          solanaReceived =
            transaction.meta.postBalances[2] - transaction.meta.preBalances[2];
        }

        const payedSolanaQuantity = solanaReceived / LAMPORTS_PER_SOL;

        return {
          signature,
          buyAtDate,
          walletAddress,
          fee,
          payedSolanaQuantity,
          tokenQuantity,
          status,
        };
      }
    } catch (error) {
      console.error("Failed to get Tx details: ", error);
    }
  };

  const getTransactionStatus = async (signature) => {
    try {
      const status = await connection.getSignatureStatus(signature);

      if (status?.value?.err === null) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error("Transaction failed");
    }
  };

  const sendTransactionDetails = async (details) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/token/storeBuytokenFromSmartContract`,
        details,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const signature = response.data.TokenTrxSignature;
      Swal.fire({
        title: `${response.data.message}`,
        icon: response.data.status === 200 ? "success" : "error",
        showCancelButton: true,
        cancelButtonText: "Close",
        confirmButtonText: "Check on Explorer",
      }).then((result) => {
        if (result.isConfirmed) {
          if (!signature) {
            console.error("Signature not available");
            return;
          }
          const url = `${REACT_APP_EXPLORER_BASE_URL}tx/${signature}?cluster=${REACT_APP_TOKEN_ENVIRONMENT}`;

          window.open(url, "_blank", "noopener,noreferrer");
        }
      });
    } catch (error) {
      console.error("Error sending transaction details:", error);
      Swal.fire({
        title: "Error!",
        text: `Error: ${error.message}`,
        icon: "error",
        confirmButtonText: "OK",
      });
      setLoading(false);
    }
  };

  const fetchTokenLimit = async () => {
    try {
      const responseData = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/token/getTokenBuyLimit`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsLimit(responseData.data?.data?.tokenBuyLimitStatus || false);
      setTokenLimit(
        responseData.data?.data?.dailyTokenBuyLimit !== undefined
          ? responseData.data.data.dailyTokenBuyLimit
          : ""
      );
    } catch (error) {
      console.error("Failed to fetch token limit:", error);
    }
  };

  const getTodayBoughtTokens = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT_URL}/token/todayTokenBuyQuantity`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response;
  };

  useEffect(() => {
    fetchTokenLimit();
    getTokenInitializationDetails();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await getGoesOutToken();
    };

    if (!loadingState && currentSlot && totalBuyable) {
      fetchData();
    }
  }, [loadingState, currentSlot, totalBuyable]);

  const handleGetEstimatedTokens = async () => {
    if (solValue > 0) {
      const solValueNumber = parseFloat(solValue);
      const balanceNumber = parseFloat(balance);
      if (solValueNumber > balanceNumber) {
        Swal.fire({
          title: "Error",
          text: "You don't have enough SOL to proceed with this transaction.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
      const storedWallet = await fetchUserWalletAddress();
      if (walletAddress !== storedWallet) {
        Swal.fire({
          title: "Wallet Mismatch",
          text: messages.errors.walletMismatch,
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }
      try {
        if (currentSlot != null) {
          setLoading(true);
          setErrorMessage("");

          const solPrice = await getPriceDetails();

          const estimatedTokens = calculateTokenAmount(solValue, solPrice);

          setTokenValue(estimatedTokens);
          setCalculated(true);

          let todayBoughtTokens = 0;
          if (isLimit) {
            const todayTokensResponse = await getTodayBoughtTokens();
            todayBoughtTokens = todayTokensResponse?.data?.todayBought;
          }
          const totalTokenQuantity =
            parseFloat(estimatedTokens) + todayBoughtTokens;
          const remainingTokenBuyLimit = tokenLimit - todayBoughtTokens;

          if (isLimit && totalTokenQuantity > tokenLimit) {
            const response = await axios.get(
              `${process.env.REACT_APP_API_ENDPOINT_URL}/token/getRequireSolQuantity/${remainingTokenBuyLimit}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const requiredAPISOL = response.data?.data?.SolQuantity;

            const message =
              remainingTokenBuyLimit > 0
                ? `You can only buy ${remainingTokenBuyLimit} $LAL tokens for the day. Try entering ${requiredAPISOL} as SOL amount for purchasing ${remainingTokenBuyLimit} $LAL tokens`
                : `You have exceeded the token purchase limit for the day.`;

            setErrorMessage(message);
            setLoading(false);
            return;
          }
        } else {
          Swal.fire({
            title: "Sale not started",
            text: "Token Sale Not Started Yet, Please try after sometime",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        console.error("Failed to fetch tokens:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setError("Please enter an amount more than 0.");
    }
  };

  const fetchUserWalletAddress = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/users/userWalletAddress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const storedWalletAddress = response.data?.walletAddress?.walletAddress;
      return storedWalletAddress;
    } catch (error) {
      console.error("Failed to fetch user wallet address", error);
      throw error;
    }
  };

  const handleConnectWallet = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const decimalIndex = value.indexOf(".");
    const decimalPartLength =
      decimalIndex !== -1 ? value.length - decimalIndex - 1 : 0;
    if (decimalPartLength <= 9) {
      setSolValue(value);
      setError("");
      setTokenValue("");
      setCalculated(false);
    } else {
      setError("Note: Only 9 digits allowed after the decimal point");
    }
  };

  return (
    <>
      <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
        <div className="mb-3 solana-display">
          <i className="fa fa-arrow-right arrow-price"></i>
          <h6>Current Solana Price :</h6>
          <SolanaPrice />
        </div>
        <div className="mb-3 solana-display">
          <i className="fa fa-arrow-right arrow-price"></i>
          <h6>Current $LAL Price : ${tokenPrice} USD</h6>
        </div>
        {isLimit && (
          <div className="mb-3 solana-display">
            <i className="fa fa-arrow-right arrow-price"></i>
            <h6>$LAL Purchase Limit per day: {tokenLimit} $LAL</h6>
          </div>
        )}
        <Form.Label>
          Enter SOL Amount <span>*</span>
        </Form.Label>
        <Form.Control
          type="number"
          placeholder="Enter Sol Amount"
          value={solValue}
          onChange={handleInputChange}
          required
        />
      </Form.Group>
      {connected ? (
        <div className="connect-wallet-one">
          {!calculated && (
            <Button onClick={handleGetEstimatedTokens} disabled={loading}>
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                "Calculate Tokens"
              )}
            </Button>
          )}
          {tokenValue && (
            <div>
              <h6>
                Calculated Token Quantity:
                {tokenValue ? ` ${tokenValue} $LAL` : `0 $LAL`}
              </h6>
              <div>
                {errorMessage && (
                  <div className="error-message">
                    <span>
                      <i className="fa fa-info-circle" aria-hidden="true"></i>
                    </span>
                    {errorMessage}
                  </div>
                )}
              </div>
              <Button
                onClick={handleBuyTokens}
                disabled={loading || !!errorMessage}
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  "Buy Tokens"
                )}
              </Button>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
        </div>
      ) : (
        <div className="connect-wallet-one">
          <button onClick={handleConnectWallet}>Connect Wallet</button>
        </div>
      )}
    </>
  );
};
const getAssociatedTokenAccount = async (mint, owner, payer) => {
  let tokenAccountAddress = await getAssociatedTokenAddress(mint, owner, true);

  const accountInfo = await connection.getAccountInfo(tokenAccountAddress);
  if (accountInfo !== null) {
    return [tokenAccountAddress, null];
  }

  let tokenAccountCreationInstruction = createAssociatedTokenAccountInstruction(
    payer,
    tokenAccountAddress,
    owner,
    mint
  );
  return [tokenAccountAddress, tokenAccountCreationInstruction];
};

const getPriceDetails = async () => {
  const params = {
    "ids[]": [SOL_PRICE_FEED_ID],
  };

  try {
    const response = await axios.get(HERMES_URL_API, {
      params: {
        "ids[]": [SOL_PRICE_FEED_ID],
      },
    });

    const firstPrice = response.data.parsed[0];
    const rawPrice = firstPrice.price.price;
    const exponent = firstPrice.price.expo;
    const adjustedPrice = rawPrice * Math.pow(10, exponent);

    return adjustedPrice;
  } catch (error) {
    console.error("Error Fetching Price Updates: ", error);
  }
};

const calculateTokenAmount = (solValue, solPrice) => {
  const solInUSD = solValue * solPrice;
  const tokenAmount = solInUSD / tokenPrice;
  return tokenAmount;
};
